/**
 * Post-Meeting API Service
 * Handles communication with backend for FINSA-compliant documentation
 * Following SOLID principles with clean architecture
 */

import { API_BASE_URL } from '../../config/api'
import { httpClient } from '../../lib/httpClient'

// ============================================================================
// TYPES & INTERFACES (Interface Segregation)
// ============================================================================

export interface GenerateNotesRequest {
  sessionId: string
  clientType: 'retail' | 'professional' | 'institutional'
  clientName?: string
  clientId?: string
  includeEmailDraft?: boolean
}

export interface FINSANotesResponse {
  documentationType: 'advisory_minutes' | 'business_notes' | 'no_documentation'
  finsaCompliant: boolean
  requiresClientDelivery: boolean
  notes: {
    executiveSummary: string
    advisoryMinutes?: string
    businessNotes?: string
    keyInsights?: {
      keyPoints: string[]
      decisions: string[]
      nextSteps: string[]
      complianceNotes: string[]
      clientMood?: string
    }
    recommendations?: any[]
    complianceNotes?: {
      finsaArticle: string
      documentationType: string
      clientDeliveryRequired: boolean
    }
  }
  emailDraft?: string
  metadata: {
    sessionId: string
    clientType: string
    detectionDetails: any
    timestamp: string
  }
}

export interface EmailGenerationRequest {
  sessionId: string
  recipientEmail: string
  recipientName: string
  emailStyle?: 'formal_business' | 'business_friendly' | 'established_relationship'
  includeSummary?: boolean
  includeActionItems?: boolean
  // TASK-571: the real meeting summary + action items already shown in the UI, so the
  // sent email reflects the actual meeting (no server-side placeholder).
  summary?: string
  actionItems?: string[]
  // H1: the RM-reviewed subject/body — when present the backend sends them verbatim.
  subject?: string
  body?: string
}

/**
 * The `/post-meeting-data` response, as the endpoint actually returns it.
 *
 * TASK-704: this interface previously declared camelCase keys (`sessionId`, `transcriptCount`,
 * `hasDocumentation`) that the endpoint has never returned — there is no mapping layer, the
 * response is passed through verbatim. Nothing consumed those fields, so the lie was invisible
 * until something needed to read the payload. Corrected to snake_case here rather than adding a
 * mapper, because every other field on this client is already the wire shape.
 */
export interface PostMeetingData {
  session_id: string
  /** VERBATIM recorded segments only — a dictated summary is never counted here. */
  transcript_count: number
  transcripts: any[]
  documentation: any
  has_documentation: boolean
  /** TASK-704 — true when the lookup FAILED, which is not the same as there being none. */
  documentation_unavailable?: boolean
  timestamp: string
}

export interface ComplianceStatus {
  sessionId: string
  hasTranscripts: boolean
  complianceStatus: string
  requiresAdvisoryMinutes?: boolean
  investmentRecommendationsFound?: boolean
  confidenceScore?: number
  detectionDetails?: any
  timestamp: string
}

// Mirrors backend TaskResponse (post_meeting_routes_with_tasks.py). The DB `title`
// column is aliased to `task_description`; the DB `description` nests under `context`.
// `priority`/`status` are unconstrained strings server-side — narrow at the mapping site.
export interface ExtractedTask {
  id: string
  meeting_id: string
  session_id: string
  task_description: string
  assigned_to?: string | null
  due_date?: string | null
  priority: string
  status: string
  context?: Record<string, unknown> | null
  created_at: string
  updated_at?: string | null
  title?: string // legacy responses only — prefer task_description
}

// Mirrors backend LifeEventResponse (post_meeting_routes_with_tasks.py).
export interface ExtractedLifeEvent {
  id: string
  meeting_id: string
  session_id: string
  event_type: string
  event_date?: string | null
  description: string
  financial_impact?: string | null
  priority: string
  context?: Record<string, unknown> | null
  created_at: string
}

export interface ExtractedItemsResponse {
  success: boolean
  session_id: string
  tasks: ExtractedTask[]
  life_events: ExtractedLifeEvent[]
  summary?: string
  finsa_status?: 'advisory_minutes' | 'business_notes' | 'no_documentation'
  requires_delivery?: boolean
  extraction_complete: boolean
  timestamp: string
}

// ============================================================================
// API ERROR HANDLING (Single Responsibility)
// ============================================================================

class PostMeetingApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: any
  ) {
    super(message)
    this.name = 'PostMeetingApiError'
  }
}

// ============================================================================
// API SERVICE (Open/Closed Principle - extensible via interfaces)
// ============================================================================


// ============================================================================
// TASK-705 — the one meeting registry
// ============================================================================

/** Per-section state. Three, deliberately: `empty` and `failed` must never render alike. */
export type MeetingSectionState = 'available' | 'empty' | 'failed'

export interface MeetingDocumentationSource {
  /** Discriminated — a dictated summary is never returned as a verbatim transcript. */
  kind: 'recorded_transcript' | 'rm_dictated_summary' | string
  label: string
  segment_count?: number
  text?: string | null
}

export interface MeetingRecord {
  session_id: string
  meeting: {
    session_id: string
    meeting_id: string
    status: string | null
    client_id: string | null
    client_name: string | null
    capture_mode: string | null
    documentation_source: string | null
    documented_at: string | null
    scheduled_at: string | null
    started_at: string | null
    ended_at: string | null
    /** Derived from the MEETING, never from the moment a row was read. */
    retention_until: string | null
    retention_anchor: string | null
  }
  preparation: Record<string, unknown> | null
  documentation_sources: MeetingDocumentationSource[]
  finsa_documentation: Record<string, unknown> | null
  notes: Array<Record<string, unknown>>
  tasks: Array<Record<string, unknown>>
  life_events: Array<Record<string, unknown>>
  batch: Record<string, unknown> | null
  sections: Record<string, MeetingSectionState>
}

class PostMeetingApiService {
  constructor(_baseUrl: string = API_BASE_URL) {
    // baseUrl and defaultHeaders are not needed since we use httpClient
  }

  /**
   * Generate FINSA-compliant meeting notes
   * Detects if advisory minutes are required and generates appropriate documentation
   */
  async generateFINSANotes(request: GenerateNotesRequest): Promise<FINSANotesResponse> {
    console.log('🔵 [PostMeetingApi] generateFINSANotes called with:', request)

    try {
      const requestBody = {
        session_id: request.sessionId,
        client_type: request.clientType,
        client_name: request.clientName,
        client_id: request.clientId,
        include_email_draft: request.includeEmailDraft,
      }

      console.log('🔵 [PostMeetingApi] Request body:', requestBody)

      // Use httpClient which automatically adds Authorization header
      const data = await httpClient.post<any>('/api/meeting/generate-finsa-notes', requestBody)

      console.log('🔵 [PostMeetingApi] Response data received')
      return this.transformNotesResponse(data)
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to generate FINSA notes: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * Generate and send follow-up email
   */
  async sendFollowUpEmail(request: EmailGenerationRequest): Promise<{ success: boolean; emailContent: string; status: string }> {
    try {
      return await httpClient.post(`/api/meeting/${request.sessionId}/send-email`, {
        session_id: request.sessionId,
        recipient_email: request.recipientEmail,
        recipient_name: request.recipientName,
        email_style: request.emailStyle || 'business_friendly',
        include_summary: request.includeSummary !== false,
        include_action_items: request.includeActionItems !== false,
        summary: request.summary,
        action_items: request.actionItems,
        subject: request.subject,
        body: request.body,
      })
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to send follow-up email: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * H1: generate the compliant follow-up draft (subject + body) for the RM to review/edit.
   * Does NOT send — the reviewed result is sent verbatim via sendFollowUpEmail.
   */
  async generateFollowUpDraft(request: EmailGenerationRequest): Promise<{ email_subject: string; email_content: string }> {
    return await httpClient.post(`/api/meeting/${request.sessionId}/generate-email-draft`, {
      session_id: request.sessionId,
      recipient_email: request.recipientEmail,
      recipient_name: request.recipientName,
      email_style: request.emailStyle || 'business_friendly',
      summary: request.summary,
      action_items: request.actionItems,
    })
  }

  /**
   * Get all data needed for post-meeting hub
   */
  /**
   * TASK-705: the meeting and everything it produced, from ONE place, keyed by session.
   *
   * Read-only — this GET creates nothing. The endpoint it replaced lazily backfilled a shadow
   * session registry, which is how a compliance table grew by six rows in two days while
   * nobody was working on meetings.
   */
  async getMeetingRecord(sessionId: string): Promise<MeetingRecord> {
    try {
      return await httpClient.get(`/api/meeting/${sessionId}/record`)
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to get the meeting record: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  async getPostMeetingData(sessionId: string): Promise<PostMeetingData> {
    try {
      return await httpClient.get(`/api/meeting/${sessionId}/post-meeting-data`)
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to get post-meeting data: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * Generate tasks from meeting transcription
   */
  async generateTasks(sessionId: string): Promise<{ success: boolean; tasks: any[] }> {
    try {
      const data = await httpClient.post<any>(`/api/meeting/${sessionId}/generate-tasks`)
      return {
        success: true,
        tasks: data.tasks || []
      }
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to generate tasks: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * Sync a session's extracted tasks to a CRM target (TASK-516).
   * `target` defaults to 'internal' (Kielis's own client record); external
   * providers slot in backend-side without changing this call.
   */
  async syncToCRM(
    sessionId: string,
    documents: string[],
    target: string = 'internal'
  ): Promise<{
    success: boolean
    target: string
    synced: string[]
    missing: string[]
    synced_count: number
  }> {
    try {
      return await httpClient.post(`/api/meeting/${sessionId}/crm-sync`, { target, documents })
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to sync to CRM: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * Check FINSA compliance status for a meeting
   */
  async getComplianceStatus(sessionId: string): Promise<ComplianceStatus> {
    try {
      return await httpClient.get(`/api/meeting/${sessionId}/compliance-status`)
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to get compliance status: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * Get all extracted items (tasks and life events) for a meeting
   */
  async getExtractedItems(sessionId: string): Promise<ExtractedItemsResponse> {
    try {
      // Use httpClient which automatically adds Authorization header
      const data = await httpClient.get<any>(`/api/meeting/${sessionId}/extracted-items`)

      return {
        success: true,
        session_id: data.session_id || sessionId,
        tasks: data.tasks || [],
        life_events: data.life_events || [],
        summary: data.summary,
        finsa_status: data.finsa_status,
        requires_delivery: data.requires_delivery,
        extraction_complete: data.extraction_complete !== false,
        timestamp: data.timestamp || new Date().toISOString()
      }
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to get extracted items: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * Get only tasks for a meeting
   */
  async getTasks(sessionId: string): Promise<ExtractedTask[]> {
    try {
      const data = await httpClient.get<any>(`/api/meeting/${sessionId}/tasks`)
      return Array.isArray(data) ? data : (data.tasks || [])
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to get tasks: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * Get only life events for a meeting
   */
  async getLifeEvents(sessionId: string): Promise<ExtractedLifeEvent[]> {
    try {
      const data = await httpClient.get<any>(`/api/meeting/${sessionId}/life-events`)
      return Array.isArray(data) ? data : (data.life_events || [])
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to get life events: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * TASK-182: Get KYC prefill data for a client (check for pending extraction)
   */
  async getKYCPrefillData(clientId: string): Promise<any> {
    try {
      // Call data-ingestion agent's kyc-prefill endpoint
      return await httpClient.get(`/api/clients/${clientId}/kyc-prefill`)
    } catch (error: any) {
      throw new PostMeetingApiError(
        `Failed to get KYC prefill data: ${error.message || 'Unknown error'}`,
        error.response?.status,
        error
      )
    }
  }

  /**
   * Get compliance documents required for a meeting session
   */
  async getComplianceDocuments(sessionId: string): Promise<{
    documents: Array<{
      id: string;
      title: string;
      documentType: string;
      signatureStatus: string;
      status: string;
      description: string;
    }>;
    disclosures: Array<{
      id: string;
      label: string;
      required: boolean;
      completed: boolean;
    }>;
    meeting_type: string;
  }> {
    try {
      return await httpClient.get(`/api/compliance-review/${sessionId}/documents`);
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error('Unknown error');
      throw new PostMeetingApiError(
        `Failed to get compliance documents: ${err.message}`,
        (error as Record<string, any>)?.response?.status,
        error
      );
    }
  }

  /**
   * Save compliance review / signature status to backend
   * Uses POST /compliance-review/save endpoint
   */
  async saveComplianceReview(data: {
    session_id: string;
    compliance_review: string;
    compliance_score?: number;
    documents_analyzed?: number;
    document_ids?: string[];
  }): Promise<{ success: boolean }> {
    try {
      return await httpClient.post('/api/compliance-review/save', data);
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error('Unknown error');
      throw new PostMeetingApiError(
        `Failed to save compliance review: ${err.message}`,
        (error as Record<string, any>)?.response?.status,
        error
      );
    }
  }

  /**
   * Transform API response to frontend format
   * Ensures backward compatibility and consistent structure
   */
  private transformNotesResponse(data: any): FINSANotesResponse {
    return {
      documentationType: data.documentation_type || 'no_documentation',
      finsaCompliant: data.finsa_compliant !== false,
      requiresClientDelivery: data.requires_client_delivery || false,
      notes: {
        executiveSummary: data.notes?.executive_summary || data.notes?.executiveSummary || '',
        advisoryMinutes: data.notes?.advisory_minutes || data.notes?.advisoryMinutes,
        businessNotes: data.notes?.business_notes || data.notes?.businessNotes,
        keyInsights: data.notes?.key_insights || data.notes?.keyInsights,
        recommendations: data.notes?.recommendations,
        complianceNotes: data.notes?.compliance_notes || data.notes?.complianceNotes,
      },
      emailDraft: data.email_draft || data.emailDraft,
      metadata: {
        sessionId: data.metadata?.session_id || data.metadata?.sessionId || '',
        clientType: data.metadata?.client_type || data.metadata?.clientType || 'retail',
        detectionDetails: data.metadata?.detection_details || data.metadata?.detectionDetails || {},
        timestamp: data.metadata?.timestamp || new Date().toISOString(),
      },
    }
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE (Dependency Injection pattern)
// ============================================================================

export const postMeetingApi = new PostMeetingApiService()

// Export class for testing and custom configuration
export { PostMeetingApiService }

// ============================================================================
// MOCK MODE FOR DEVELOPMENT (Strategy Pattern)
// ============================================================================

export class MockPostMeetingApiService extends PostMeetingApiService {
  async generateFINSANotes(request: GenerateNotesRequest): Promise<FINSANotesResponse> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500))

    // Return mock data based on request
    const hasRecommendations = Math.random() > 0.5
    const documentationType = hasRecommendations ? 'advisory_minutes' : 'business_notes'

    return {
      documentationType: documentationType as any,
      finsaCompliant: true,
      requiresClientDelivery: hasRecommendations && request.clientType === 'retail',
      notes: {
        executiveSummary: `Mock executive summary for session ${request.sessionId}. Client ${request.clientName || 'Unknown'} discussed portfolio performance and market outlook.`,
        advisoryMinutes: hasRecommendations ? 'Mock advisory minutes with investment recommendations...' : undefined,
        businessNotes: !hasRecommendations ? 'Mock business notes without specific recommendations...' : undefined,
        keyInsights: {
          keyPoints: ['Portfolio review completed', 'Market outlook discussed', 'Risk tolerance confirmed'],
          decisions: hasRecommendations ? ['Rebalance portfolio', 'Increase equity allocation'] : [],
          nextSteps: ['Schedule follow-up in 3 months', 'Send quarterly report'],
          complianceNotes: hasRecommendations ? ['Advisory minutes required per FINSA'] : [],
          clientMood: 'positive',
        },
        recommendations: hasRecommendations ? [
          { text: 'Increase equity allocation to 65%', context: 'Based on risk profile' },
          { text: 'Add emerging markets exposure', context: 'Diversification benefit' },
        ] : [],
        complianceNotes: {
          finsaArticle: hasRecommendations ? 'Article 12' : 'N/A',
          documentationType: hasRecommendations ? 'Advisory Minutes' : 'Business Notes',
          clientDeliveryRequired: hasRecommendations && request.clientType === 'retail',
        },
      },
      emailDraft: request.includeEmailDraft ? 'Mock email draft content...' : undefined,
      metadata: {
        sessionId: request.sessionId,
        clientType: request.clientType,
        detectionDetails: {
          confidenceScore: 0.85,
          patternsMatched: hasRecommendations ? 3 : 0,
        },
        timestamp: new Date().toISOString(),
      },
    }
  }

  async sendFollowUpEmail(request: EmailGenerationRequest): Promise<{ success: boolean; emailContent: string; status: string }> {
    await new Promise(resolve => setTimeout(resolve, 1000))
    return {
      success: true,
      emailContent: `Mock email to ${request.recipientName} at ${request.recipientEmail}`,
      status: 'draft_created',
    }
  }

  async getPostMeetingData(sessionId: string): Promise<PostMeetingData> {
    await new Promise(resolve => setTimeout(resolve, 500))
    // TASK-704: mirrors the real wire shape (snake_case). It previously returned camelCase
    // keys the endpoint never produces, so a component developed against the mock would break
    // against the service.
    return {
      session_id: sessionId,
      transcript_count: 42,
      transcripts: [],
      documentation: null,
      has_documentation: false,
      documentation_unavailable: false,
      timestamp: new Date().toISOString(),
    }
  }

  async getComplianceStatus(sessionId: string): Promise<ComplianceStatus> {
    await new Promise(resolve => setTimeout(resolve, 500))
    return {
      sessionId,
      hasTranscripts: true,
      complianceStatus: 'advisory_minutes',
      requiresAdvisoryMinutes: true,
      investmentRecommendationsFound: true,
      confidenceScore: 0.85,
      detectionDetails: {},
      timestamp: new Date().toISOString(),
    }
  }
}

// Use mock service in development
export const mockPostMeetingApi = new MockPostMeetingApiService()